import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Facebook, Youtube } from 'lucide-react';

const WhatsAppIcon = ({ size = 18, className = "" }: { size?: number, className?: string }) => (
  <svg 
    viewBox="0 0 24 24" 
    width={size} 
    height={size} 
    fill="currentColor"
    className={className}
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

export default function Footer() {
  const { t } = useTranslation();

  return (
    <footer className="bg-stone-50 border-t border-stone-200">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-12 px-8 py-16 max-w-7xl mx-auto">
        <div className="space-y-6">
          <div className="text-xl font-bold text-emerald-950 font-headline"><img src="/assets/Footer Logo.png" alt="Anaaj.ai - AI Farming Assistant Logo" className="h-16 w-auto object-contain" /></div>
          <p className="text-stone-500 font-body text-sm leading-relaxed">
            {t('footer.mission')}
          </p>
        </div>
        
        <div>
          <h5 className="text-emerald-900 font-bold mb-6">{t('footer.company')}</h5>
          <ul className="space-y-4">
            <li><Link className="text-stone-500 hover:text-emerald-700 hover:translate-x-1 transition-all inline-block font-body text-sm" to="/about">{t('nav.about')}</Link></li>
            <li><Link className="text-stone-500 hover:text-emerald-700 hover:translate-x-1 transition-all inline-block font-body text-sm" to="/blog">{t('nav.blogWithGuides')}</Link></li>
            <li><Link className="text-stone-500 hover:text-emerald-700 hover:translate-x-1 transition-all inline-block font-body text-sm" to="/sustainability">{t('footer.sustainabilityReport')}</Link></li>
            <li><Link className="text-stone-500 hover:text-emerald-700 hover:translate-x-1 transition-all inline-block font-body text-sm" to="/sitemap">{t('footer.sitemap')}</Link></li>
          </ul>
        </div>
        
        <div>
          <h5 className="text-emerald-900 font-bold mb-6">{t('footer.resources')}</h5>
          <ul className="space-y-4">
            <li><Link className="text-stone-500 hover:text-emerald-700 hover:translate-x-1 transition-all inline-block font-body text-sm" to="/blog?category=how-to">{t('footer.howToGuides')}</Link></li>
            <li><Link className="text-stone-500 hover:text-emerald-700 hover:translate-x-1 transition-all inline-block font-body text-sm" to="/blog?category=success-story">{t('footer.farmerStories')}</Link></li>
            <li><Link className="text-stone-500 hover:text-emerald-700 hover:translate-x-1 transition-all inline-block font-body text-sm" to="/blog?category=education">{t('footer.learnFarming')}</Link></li>
            <li><Link className="text-stone-500 hover:text-emerald-700 hover:translate-x-1 transition-all inline-block font-body text-sm" to="/download">{t('nav.downloadApp')}</Link></li>
          </ul>
        </div>
        
        <div>
          <h5 className="text-emerald-900 font-bold mb-6">{t('footer.supportLegal')}</h5>
          <ul className="space-y-4">
            <li><Link className="text-stone-500 hover:text-emerald-700 hover:translate-x-1 transition-all inline-block font-body text-sm" to="/contact">{t('footer.contactSupport')}</Link></li>
            <li><Link className="text-stone-500 hover:text-emerald-700 hover:translate-x-1 transition-all inline-block font-body text-sm" to="/privacy">{t('footer.privacyPolicy')}</Link></li>
            <li><Link className="text-stone-500 hover:text-emerald-700 hover:translate-x-1 transition-all inline-block font-body text-sm" to="/terms">{t('footer.termsOfService')}</Link></li>
          </ul>
          <div className="flex gap-4 mt-6">
            <a href="https://www.youtube.com/@anaajrajasthan01" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center hover:bg-[#FF0000] hover:text-white transition-all shadow-sm" aria-label="YouTube">
              <Youtube size={18} fill="currentColor" />
            </a>
            <a href="https://whatsapp.com/channel/0029VbCdUxoBKfi8x9xbIP0m" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center hover:bg-[#25D366] hover:text-white transition-all shadow-sm" aria-label="WhatsApp">
              <WhatsAppIcon size={20} />
            </a>
            <a href="#" className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center hover:bg-[#1877F2] hover:text-white transition-all shadow-sm" aria-label="Facebook">
              <Facebook size={18} fill="currentColor" />
            </a>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-8 py-8 border-t border-stone-100 flex flex-col md:flex-row justify-between items-center gap-4">
        <p className="text-stone-500 font-body text-sm tracking-wide">{t('footer.copyright')}</p>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-lime-500 animate-pulse"></span>
          <span className="text-xs font-bold text-stone-500 uppercase tracking-tighter">{t('footer.systemsOperational')}</span>
        </div>
      </div>
    </footer>
  );
}
